import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Icon } from '../components/ui/Icon';

export default function CompliancePage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      {/* Hero */}
      <section className="bg-tea-700 text-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-tea-300 text-sm font-semibold uppercase tracking-widest mb-4">Ingredients & Safety</p>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">
            Clean beauty, clearly explained
          </h1>
          <p className="mt-4 text-tea-200 text-lg max-w-xl leading-relaxed">
            AB8 Beauty products are topical skincare made with science-backed actives and ethically
            sourced botanicals. Here's what's in them, how to use them safely, and the promises we
            stand behind.
          </p>
          <div className="mt-8">
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 bg-white text-tea-800 font-semibold px-6 py-3 rounded-lg shadow-sm hover:bg-tea-50 transition-colors"
            >
              <Icon name="leaf" className="w-5 h-5 text-tea-600" aria-hidden="true" />
              Shop the collection
            </Link>
          </div>
        </div>
      </section>

      {/* Key notice */}
      <div className="bg-amber-50 border-b border-amber-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex gap-3">
            <Icon name="leaf" className="w-6 h-6 shrink-0 text-tea-600" aria-hidden="true" />
            <p className="text-amber-900 text-sm leading-relaxed">
              <strong>FOR EXTERNAL COSMETIC USE.</strong> AB8 Beauty products are topical skincare and
              cosmetics intended to cleanse, beautify, and improve the appearance of skin and hair.
              These statements have not been evaluated by the U.S. Food and Drug Administration, and
              our products are not intended to diagnose, treat, cure, or prevent any disease. We
              recommend a patch test before first use and discontinuing use if irritation occurs.
            </p>
          </div>
        </div>
      </div>

      <main className="flex-1 py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-10 mb-16">
            {/* Our commitments */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-3">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-tea-100 text-tea-700 text-sm font-bold">✓</span>
                Our Commitments
              </h2>
              <ul className="space-y-3 text-gray-700 text-[15px]">
                {[
                  'Clean formulas — no parabens or sulfates',
                  'Vegan and never tested on animals',
                  'Ethically sourced, boho-luxe botanicals',
                  'Science-backed actives at meaningful levels',
                  'Transparent ingredient lists on every product',
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="text-tea-600 mt-0.5 shrink-0">●</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* How to patch test */}
            <div className="bg-tea-50 rounded-2xl p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-3">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-tea-100 text-tea-700 text-sm font-bold">♡</span>
                How to Patch Test
              </h2>
              <ul className="space-y-3 text-gray-700 text-[15px]">
                {[
                  'Apply a small amount to your inner forearm.',
                  'Wait 24 hours without washing the area.',
                  'If no redness, itching, or irritation appears, you\'re good to go.',
                  'Discontinue use if any irritation occurs, and consult a professional if it persists.',
                  'Keep all products out of reach of children and avoid the eye area.',
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="text-tea-600 mt-0.5 shrink-0">●</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Sections */}
          <div className="flex flex-col gap-12">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Key Ingredients</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                <strong>GHK-CU copper peptides</strong> are naturally occurring copper-binding peptides
                studied for their role in supporting skin's appearance — helping skin look firmer,
                smoother, and more youthful when applied topically.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                <strong>Bee venom</strong> is a natural, luxurious active traditionally used in beauty
                to help promote a plumped, radiant-looking complexion. As with any active, we recommend
                a patch test, and we don't recommend bee venom products if you have a known bee allergy.
              </p>
              <p className="text-gray-600 leading-relaxed">
                <strong>Boho-luxe botanicals</strong> round out our formulas with ethically sourced
                plant ingredients chosen for nature's nourishing, skin-loving properties.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Cosmetic Use & Regulation</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                AB8 Beauty products are cosmetics intended for external use on the skin and hair. In
                the United States, cosmetics are regulated by the FDA, but — unlike drugs — they are
                not pre-approved before sale. Our products are not drugs and are not intended to
                diagnose, treat, cure, or prevent any disease or condition.
              </p>
              <p className="text-gray-600 leading-relaxed">
                If you have a medical concern about your skin, or you're pregnant, nursing, or under a
                doctor's care, please consult a qualified healthcare professional before adding new
                actives to your routine.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Referral Program Guidelines</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                AB8 Beauty members who share products through our referral program agree to a few simple
                rules that keep things honest:
              </p>
              <ul className="space-y-3 text-gray-600 text-[15px] mb-4">
                {[
                  'Disclose your connection to AB8 Beauty in any promotional content (as required by FTC endorsement guidelines).',
                  'Don\'t make medical, therapeutic, or disease-treatment claims about our products.',
                  'Share only honest experiences and accurate product descriptions.',
                  'Never represent that our products are FDA-approved drugs.',
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="text-tea-600 mt-0.5 shrink-0">●</span>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-gray-600 leading-relaxed">
                Violating these guidelines may result in account suspension and forfeiture of
                accumulated referral cash.
              </p>
            </section>

            <section className="bg-tea-50 rounded-2xl p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Questions?</h2>
              <p className="text-gray-600 mb-5 text-[15px] leading-relaxed">
                If you have questions about ingredients, how to use a product, or the referral program,
                our team is happy to help.
              </p>
              <a href="mailto:support@ab8beauty.com?subject=Product%20Question" className="btn-primary">
                Contact Us
              </a>
              <div className="mt-6 flex flex-wrap gap-4 text-sm">
                <Link to="/terms" className="text-tea-700 font-medium hover:underline">Terms of Service</Link>
                <Link to="/privacy" className="text-tea-700 font-medium hover:underline">Privacy Policy</Link>
                <Link to="/support" className="text-tea-700 font-medium hover:underline">Support FAQ</Link>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
